import React, { useState, useEffect, useRef } from 'react';
import styles from './ContactUs.module.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FaEnvelope, FaPhoneAlt, FaMapMarkerAlt,
  FaLinkedin, FaTwitter, FaInstagram, FaPaperPlane, FaChevronDown
} from 'react-icons/fa';
import {  FaFacebookF,FaLinkedinIn, FaYoutube } from 'react-icons/fa';

// Firebase
import { textdb } from '../../firebase'; // Adjust path if needed
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

gsap.registerPlugin(ScrollTrigger);

const faqData = [
  {
    id: 'faq1',
    question: "What digital marketing services do you offer?",
    answer: "We offer a comprehensive suite of services including SEO, PPC advertising, social media marketing, content creation, email marketing, and web design & development. Our strategies are tailored to meet your specific business goals."
  },
  {
    id: 'faq2',
    question: "How quickly can I expect to see results?",
    answer: "Results vary depending on the services and your current digital presence. SEO can take 3-6 months for significant impact, while PPC can show results within days. We focus on sustainable, long-term growth."
  },
  {
    id: 'faq3',
    question: "What is your pricing structure?",
    answer: "Our pricing is customized based on your project's scope and requirements. We offer project-based pricing as well as monthly retainers. Contact us for a detailed proposal tailored to your needs."
  },
  {
    id: 'faq4',
    question: "Do you work with small businesses?",
    answer: "Absolutely! We love partnering with businesses of all sizes, from startups to established enterprises. Our flexible approach allows us to adapt our strategies to fit different budgets and objectives."
  }
];

const FAQItem = ({ faq, isOpen, onToggle }) => {
  const answerRef = useRef(null);
  const chevronRef = useRef(null);

  useEffect(() => {
    if (!answerRef.current || !chevronRef.current) return; // Guard clause
    if (isOpen) {
      gsap.to(answerRef.current, {
        height: 'auto',
        opacity: 1,
        duration: 0.3,
        ease: 'power1.inOut',
        paddingTop: '10px', // Example padding
        paddingBottom: '20px'
      });
      gsap.to(chevronRef.current, { rotate: 180, duration: 0.3 });
    } else {
      gsap.to(answerRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power1.inOut',
        paddingTop: 0,
        paddingBottom: 0
      });
      gsap.to(chevronRef.current, { rotate: 0, duration: 0.3 });
    }
  }, [isOpen]);

  return (
    <div className={styles.faqItem}>
      <div
        className={styles.faqQuestion}
        onClick={onToggle}
      >
        {faq.question}
        <div ref={chevronRef}>
          <FaChevronDown />
        </div>
      </div>
      <div ref={answerRef} className={styles.faqAnswer} style={{ height: 0, opacity: 0, overflow: 'hidden' }}>
        <p>{faq.answer}</p>
      </div>
    </div>
  );
};


const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState(''); // For Firebase errors
  const [openFAQ, setOpenFAQ] = useState(null);

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const mainContentRef = useRef(null);
  const formSectionRef = useRef(null);
  const contactInfoSectionRef = useRef(null);
  const faqSectionRef = useRef(null);
  const successMessageRef = useRef(null);
  const submitButtonRef = useRef(null);

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      if (headerRef.current) {
        gsap.from(headerRef.current, {
          opacity: 0, y: -50, duration: 0.7, ease: "easeOut"
        });
      }

      const animateInFromBottom = (elem, delay = 0, staggerAmount = 0) => {
        if (!elem || (Array.isArray(elem) && elem.some(el => !el)) || (!Array.isArray(elem) && !elem)) return;
        gsap.from(elem, {
          opacity: 0, y: 20, duration: 0.5, ease: "easeOut", delay, stagger: staggerAmount,
          scrollTrigger: {
            trigger: Array.isArray(elem) ? elem[0] : elem, // Use first element as trigger if array
            start: "top 85%", once: true,
          }
        });
      };

      animateInFromBottom([formSectionRef.current, contactInfoSectionRef.current].filter(Boolean), 0, 0.2);

      if (formSectionRef.current) {
        const formGroups = Array.from(formSectionRef.current.querySelectorAll(`.${styles.formGroup}`));
        animateInFromBottom(formGroups.filter(Boolean), 0.2, 0.1);
      }
      if (submitButtonRef.current) animateInFromBottom(submitButtonRef.current, 0.5);

      if (contactInfoSectionRef.current) {
        const infoItems = Array.from(contactInfoSectionRef.current.querySelectorAll(`.${styles.infoItem}`));
        const socialIconsContainer = contactInfoSectionRef.current.querySelector(`.${styles.socialIcons}`);
        animateInFromBottom(infoItems.filter(Boolean), 0.2, 0.1);
        if (socialIconsContainer) {
            const socialIcons = Array.from(socialIconsContainer.querySelectorAll('a'));
            animateInFromBottom(socialIcons.filter(Boolean), 0.4, 0.1);
        }
      }

      if (faqSectionRef.current) {
        const faqTitleEl = faqSectionRef.current.querySelector(`.${styles.faqTitle}`);
        if (faqTitleEl) animateInFromBottom(faqTitleEl, 0);
        const faqItems = Array.from(faqSectionRef.current.querySelectorAll(`.${styles.faqItem}`));
        animateInFromBottom(faqItems.filter(Boolean), 0.2, 0.1);
      }

      if (submitButtonRef.current) {
        const button = submitButtonRef.current;
        const enterAnim = gsap.to(button, { scale: 1.05, duration: 0.2, paused: true });
        const leaveAnim = gsap.to(button, { scale: 1, duration: 0.2, paused: true });
        const downAnim = gsap.to(button, { scale: 0.95, duration: 0.1, paused: true });
        const upAnim = gsap.to(button, { scale: 1.05, duration: 0.1, paused: true });

        button.addEventListener('mouseenter', () => enterAnim.play(0));
        button.addEventListener('mouseleave', () => leaveAnim.play(0));
        button.addEventListener('mousedown', () => downAnim.play(0));
        button.addEventListener('mouseup', () => upAnim.play(0));
      }
    }, pageRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!successMessageRef.current) return;
    if (isSubmitted) {
      gsap.set(successMessageRef.current, { height: 'auto', display: 'block', opacity: 0, y: -20 });
      gsap.to(successMessageRef.current, { opacity: 1, y: 0, duration: 0.4, ease: 'power1.out' });
    } else if (gsap.getProperty(successMessageRef.current, "opacity") > 0) {
      gsap.to(successMessageRef.current, {
        opacity: 0, y: -20, duration: 0.4, ease: 'power1.in',
        onComplete: () => gsap.set(successMessageRef.current, { display: 'none' })
      });
    }
  }, [isSubmitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
    if (submitError) setSubmitError('');
  };

  const validateForm = () => {
    let formErrors = {};
    if (!formData.name.trim()) formErrors.name = "Full Name is required.";
    if (!formData.email.trim()) formErrors.email = "Email Address is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) formErrors.email = "Email address is invalid.";
    if (!formData.subject.trim()) formErrors.subject = "Subject is required.";
    if (!formData.message.trim()) formErrors.message = "Message is required.";
    return formErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const formErrors = validateForm();
    if (Object.keys(formErrors).length === 0) {
      setIsLoading(true);
      try {
        // Add a new document with a generated id.
        await addDoc(collection(textdb, "contactSubmissions"), {
          ...formData,
          submittedAt: serverTimestamp() // Adds a server-side timestamp
        });
        console.log('Form Data Submitted to Firebase');
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setErrors({});
        setTimeout(() => setIsSubmitted(false), 6000);
      } catch (error) {
        console.error("Error adding document: ", error);
        setSubmitError("Failed to send message. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(formErrors);
      setIsSubmitted(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className={styles.contactPage} ref={pageRef}>
      <div style={{width:'100%' , height:"100%"}}>
        <img style={{width:'100%' , height:"100%"}} src='https://res.cloudinary.com/ddkyeuhk8/image/upload/v1747508172/SIDE%20BANNER/viahoxupd6zjq0zt0tz5.png' alt='hero' />
      </div>
      {/* <header className={styles.header} ref={headerRef}>
        <h1 className={styles.title}>Let's Create Something <span>Amazing</span>.</h1>
        <p className={styles.subtitle}>
          Ready to elevate your digital presence? We're excited to learn about your project and discuss how our expertise can drive your success. Reach out, and let's start the conversation.
        </p>
      </header> */}

      <div className={styles.mainContent} ref={mainContentRef}>
        <div className={styles.formSection} ref={formSectionRef}>
          <h2>Send Us a Message</h2>
          <div ref={successMessageRef} className={styles.successMessage} style={{ display: 'none' }}>
            Thank you! Your message has been sent. We'll be in touch shortly.
          </div>
          {submitError && <p className={styles.errorMessage} style={{marginBottom: '15px', textAlign: 'center'}}>{submitError}</p>}
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formGroup}>
              <label htmlFor="name">Full Name</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Name" required />
              {errors.name && <p className={styles.errorMessage}>{errors.name}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email" required />
              {errors.email && <p className={styles.errorMessage}>{errors.email}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="phone">Phone Number (Optional)</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="Mobile Number" />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="subject">Subject</label>
              <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} placeholder="" required />
              {errors.subject && <p className={styles.errorMessage}>{errors.subject}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="message">Your Message</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="" required ></textarea>
              {errors.message && <p className={styles.errorMessage}>{errors.message}</p>}
            </div>
            <button ref={submitButtonRef} type="submit" className={styles.submitButton} disabled={isLoading}>
              {isLoading ? "Sending..." : "Send Message"}
              {!isLoading && <FaPaperPlane />}
            </button>
          </form>
        </div>

        <div className={styles.contactInfoSection} ref={contactInfoSectionRef}>
          <h2>Direct Contact</h2>
          <div className={styles.infoItem}>
            <FaEnvelope />
            <div><strong>Email Us</strong><br /><a href="mailto:connect@yourdigitalagency.com">info@digisavvyy.com</a></div>
          </div>
          <div className={styles.infoItem}>
            <FaPhoneAlt />
            <div><strong>Call Us</strong><br /><a href="tel:+919818825449">+919818825449 </a><a href="tel:+918700979978">+918700979978 </a></div>
          </div>
          <div className={styles.infoItem}>
            <FaMapMarkerAlt />
            <div><strong>Our Office</strong><br /><span>945, Rani Bagh, Pitampura, Delhi - 110034</span></div>
          </div>
          <div className={styles.socialLinks}>
            <h3 className={styles.socialLinksTitle}>Follow Our Journey</h3>
            <div className={styles.socialIcons}>
               <a href="https://www.facebook.com/profile.php?id=61552677833279" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                              <FaFacebookF />
                            </a>
                            <a href="https://instagram.com/digisavvyy?igshid=MzRlODBiNWFlZA==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                              <FaInstagram />
                            </a>
                            <a href="https://www.linkedin.com/company/digi-savvy/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                              <FaLinkedinIn />
                            </a>
                            <a href="https://www.youtube.com/@DIGISAVVYy/about" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                              <FaYoutube />
                            </a>
            </div>
          </div>
        </div>
      </div>

      <section className={styles.faqSection} ref={faqSectionRef}>
        <h2 className={styles.faqTitle}>Frequently Asked <span>Questions</span></h2>
        {faqData.map((faq, index) => (
          <FAQItem
            key={faq.id}
            faq={faq}
            isOpen={openFAQ === index}
            onToggle={() => toggleFAQ(index)}
          />
        ))}
      </section>
    </div>
  );
};

export default ContactUs;